/**
 * /start/agreement — funnel step 2 (Review & Accept the Service Agreement).
 *
 * Renders the 10 server-authoritative agreement sections (GET
 * /funnel/agreement, review F6) as an accordion, a PDF download link, and
 * the accept/back actions from spec §3.6. On accept the client echoes the
 * displayed version; a 409 means the agreement changed under the user —
 * we refetch and show a notice instead of recording a stale acceptance.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Download, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FunnelLayout } from '@/components/funnel/FunnelLayout';
import { useAgreement, FUNNEL_STATE_QUERY_KEY } from '@/hooks/useFunnel';
import { apiClient } from '@/services/api-client';
import { cn } from '@/lib/utils';

export function AgreementPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: agreement, isLoading, isError, refetch } = useAgreement();

  const [openSection, setOpenSection] = useState<number | null>(0);
  const [isAccepting, setIsAccepting] = useState(false);
  const [error, setError] = useState('');
  const [versionNotice, setVersionNotice] = useState(false);

  const handleAccept = async () => {
    if (!agreement) return;

    setError('');
    setVersionNotice(false);
    setIsAccepting(true);

    const response = await apiClient.post('/api/v1/funnel/agreement/accept', {
      displayedVersion: agreement.version,
    });

    setIsAccepting(false);

    if (!response.success) {
      if (response.error?.code === 'CONFLICT') {
        // Stale version (409) — reload the current agreement and ask the
        // user to review again before accepting.
        setVersionNotice(true);
        refetch();
        return;
      }
      setError(response.error?.message || 'Failed to record your acceptance. Please try again.');
      return;
    }

    await queryClient.invalidateQueries({ queryKey: FUNNEL_STATE_QUERY_KEY });
    navigate('/start/products');
  };

  return (
    <FunnelLayout step={2}>
      <div className="mb-6 text-center">
        <h1 className="font-display text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
          Review &amp; Accept Your Legacy Orb Service Agreement
        </h1>
        <p className="mt-2 text-muted-foreground">
          Please review the terms that govern your Legacy Orb services before continuing to
          payment. You can download a copy for your records.
        </p>
      </div>

      {versionNotice && (
        <div className="mb-4 rounded-md border border-border bg-muted/50 p-4 text-sm text-foreground">
          The Service Agreement has been updated since you opened this page. The latest version is
          shown below — please review it and accept again.
        </div>
      )}

      <Card className="mb-6">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-lg">
            Service Agreement
            {agreement && (
              <span className="ml-2 align-middle text-xs font-normal text-muted-foreground">
                Version {agreement.version}
              </span>
            )}
          </CardTitle>
          {agreement && (
            <a
              href={agreement.pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-foreground underline-offset-4 hover:underline"
            >
              <Download className="h-4 w-4" />
              Download the Service Agreement (PDF)
            </a>
          )}
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : isError || !agreement ? (
            <div className="flex flex-col items-center gap-3 py-8 text-center">
              <p className="text-sm text-destructive">Failed to load the Service Agreement.</p>
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                Try Again
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-border rounded-md border border-border">
              {agreement.sections.map((section, idx) => {
                const isOpen = openSection === idx;
                return (
                  <div key={section.title}>
                    <button
                      type="button"
                      className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm font-medium text-foreground transition-colors hover:bg-muted/50"
                      aria-expanded={isOpen}
                      onClick={() => setOpenSection(isOpen ? null : idx)}
                    >
                      <span>
                        {idx + 1}. {section.title}
                      </span>
                      <ChevronDown
                        className={cn(
                          'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
                          isOpen && 'rotate-180'
                        )}
                      />
                    </button>
                    {isOpen && (
                      <p className="px-4 pb-4 text-sm leading-relaxed text-muted-foreground">
                        {section.body}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {error && <p className="mb-4 text-center text-sm text-destructive">{error}</p>}

      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-between">
        <Button variant="outline" size="lg" onClick={() => navigate('/start/account')}>
          Back to Account Details
        </Button>
        <Button size="lg" onClick={handleAccept} disabled={!agreement || isAccepting}>
          {isAccepting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Accept Agreements &amp; Continue to Payment
        </Button>
      </div>
    </FunnelLayout>
  );
}
